/**
 * DOM utilities for facebook https://www.facebook.com/settings/?tab=*** Pages
 *
 * These pages tend to be constructed with a pane with @aria-label "Settings"
 *
 * Within the pane, the settings are organized into rows that include
 * (a) a label, and
 * (b) either a "button" or a "toggle"
 *
 * Clicking the toggle directly changes the settings value; the button
 * opens a modal that we then select a setting on.
 */
import * as Sentry from '@sentry/react'

import { _debug } from '$extensionSrc/utils/logging'

import SectionNotFoundException from '../exception/sectionNotFoundException'
import SettingNotFoundException from '../exception/settingNotFoundException'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveXPathTextSelector,
  getCommonAncestor,
  getNearestElementMatch,
} from '../interruptibleXpath'
import { getElement, getOptionalElement } from '../utils'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  clickRowMatchingPrivacySettingInDialog,
  isPrivacySettingUndoable,
  PrivacySetting,
} from './fbSelectAudienceModalUtils'

/**
 * Common utilities
 */

export async function getSettingsPane(): Promise<HTMLElement> {
  return getElement<HTMLElement>(
    document,
    `//div[@role="main" and ${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      'settings',
    )}]`,
  )
}

/**
 * Debugging utilities
 */

/**
 * For use when we fail to find label elements; provides a way to query for and
 * dump (a) the expected text and (b) all found, possibly matching, text.
 *
 * This will hopefully aid in debugging in the future.
 */
function logFoundElements(
  expectedText: string,
  getElementsFunc: () => HTMLElement[],
): void {
  try {
    const foundText = getElementsFunc().map((x) => x.textContent)
    Sentry.setContext('Automation - Found Text', {
      expectedText,
      foundText: foundText.slice(0, 50),
      foundTextFullLength: foundText.length,
    })
  } catch (error) {
    // Log to Sentry, but don't block execution
    Sentry.captureException(error)
  }
}

/**
 * Main methods
 */

async function closeDialog(dialog) {
  const closeButton = evaluateXpath<HTMLElement>(
    dialog,
    `descendant::*[@role="button" and ${getCaseInsensitiveXPathTextSelector('aria-label', 'close')}]`,
  )[0]

  if (!closeButton) {
    throw new Error('Could not find close button in dialog')
  }

  closeButton.click()
}

export async function selectTargetSettinginDialog(
  targetSetting: string,
  isDry: boolean,
): Promise<void> {
  const dialog = await getElement<HTMLElement>(
    document,
    '//*[@role = "dialog" and @aria-labelledby]',
  )

  const rows = evaluateXpath<HTMLElement>(
    dialog,
    'descendant::*[(@role="radio" or @role="none" or @type="radio") and @aria-checked]',
  )

  if (rows.length === 0) {
    throw new SectionNotFoundException(
      `selectTargetSettinginDialog: Could not find settings rows in dialog for targetSetting ${targetSetting}`,
    )
  }

  const dialogGonePromise = promises.getDomPromise(
    document,
    () =>
      evaluateXpath(document, '//*[@role = "dialog" and @aria-labelledby]')
        .length === 0,
  )
  for (let i = 0; i < rows.length; i += 1) {
    const targetRow = rows[i]
    const label =
      targetRow.role === 'radio' || targetRow.role === 'none'
        ? targetRow.textContent
        : selectAudienceUtils.getPrivacySettingFromSurroundingText(targetRow)
            .name

    if (label === targetSetting) {
      if (!isDry) {
        targetRow.click()
        _debug(
          'clicked on target settings value in settings modal',
          'fbSettingsUtils',
        )
        const saveButton = await getElement<HTMLElement>(
          dialog,
          '//div[@aria-label="Save" and @role="button"]',
        )
        saveButton.click()
        _debug('clicked on save button in settings modal', 'fbSettingsUtils')
      } else {
        await closeDialog(dialog)
      }
    }
  }
  _debug('waiting for settings modal to close', 'fbSettingsUtils')
  await dialogGonePromise
  _debug('waiting for settings modal has closed', 'fbSettingsUtils')

  // Brief wait, to try not to get Facebook mad at us
  await promises.wait(500)
}

export async function getSettingRowWithToggle(text: string) {
  const settingsPane = await getSettingsPane()
  const settingLabel = await getElement<HTMLElement>(
    settingsPane,
    `descendant::*[contains(text(),"${text}")]`,
  ).catch((error) => {
    logFoundElements(text, () =>
      evaluateXpath(settingsPane, 'descendant::*[text()]'),
    )
    throw new SectionNotFoundException(error.message)
  })

  const settingToggle = getNearestElementMatch(
    settingLabel,
    `*[@role="switch" and @type="checkbox"]`,
    'following',
  )

  if (!settingToggle) {
    throw new SettingNotFoundException(`Setting toggle not found for ${text}`)
  }

  const row = getCommonAncestor(settingLabel, settingToggle)

  if (!row) {
    throw new SettingNotFoundException('Setting row with toggle not found')
  }

  return row
}

export async function clickSettingButton(label: string) {
  const settingsPane = await getSettingsPane()
  const button = await getElement<HTMLElement>(
    settingsPane,
    `descendant::div[${getCaseInsensitiveXPathTextSelector(
      'aria-label',
      label.toLowerCase(),
    )} and @role="button"]`,
  )

  if (!button) {
    throw new Error(
      `Could not find button for Facebook settings label ${label}`,
    )
  }
  button.click()
  await promises.wait(200)
}

async function getSettingToggle(rootNode: Node) {
  const toggle = await getElement<HTMLInputElement>(
    rootNode,
    'descendant::input[@role="switch" and @type="checkbox"]',
  )

  if (!toggle) {
    throw new SettingNotFoundException(
      `Could not find toggle for privacy setting`,
    )
  }

  return toggle
}

export async function getSettingToggleValue(rootNode: Node) {
  const toggle = await getSettingToggle(rootNode)
  if (!toggle) {
    throw new SettingNotFoundException(
      `Could not find toggle for Facebook settings`,
    )
  }
  return toggle.checked
}

export async function setSettingToggleValue(rootNode: Node, on: boolean) {
  const toggle = await getSettingToggle(rootNode)
  if (!toggle) {
    throw new Error(`Could not find toggle to set for Facebook settings`)
  }

  if (on !== toggle.checked) {
    toggle.click()
  }
}

/**
 * Jan 2024
 * New utilities for clickable setting rows (no edit privacy button)
 * that open a modal with privacy setting options
 */

export async function getClickableSettingRow(textOptions: string[]) {
  const settingsPane = await getSettingsPane()

  const matchConditions = textOptions
    .map((x) => `contains(text(), "${x}")`)
    .join(' or ')
  const settingRow = await getOptionalElement<HTMLElement>(
    settingsPane,
    `.//div[@role="button" and descendant::span[${matchConditions}]]`,
  )

  if (!settingRow) {
    throw new SettingNotFoundException('Clickable setting row not found')
  }

  return settingRow
}

export async function openClickableSettingModal(labels: string[]) {
  const settingRow = await getClickableSettingRow(labels)

  const hasDialogAppeared = promises.getDomPromise(
    document,
    () => evaluateXpath(document, '//*[@role = "dialog"]').length > 0,
  )

  settingRow.click()
  await hasDialogAppeared
  _debug('settings modal has appeared after click', 'fbSettingsUtils')

  const hasLoading = (node) =>
    evaluateXpath(node, '//*[@role = "dialog" and @aria-label = "Loading..."]')
      .length > 0
  if (hasLoading(document)) {
    await promises.getDomPromise(document, () => !hasLoading(document))
  }
  _debug('settings modal confirmed no longer loading', 'fbSettingsUtils')
}

export async function getSettingModalSelectedOption(
  modalLabel: string,
): Promise<HTMLElement> {
  const dialog = await getElement<HTMLElement>(
    document,
    `//*[@role="dialog" and descendant::*[contains(text(),"${modalLabel}")]]`,
  )

  return getElement<HTMLElement>(
    dialog,
    'descendant::*[(@role="radio" or @role="none" or @type="radio") and @aria-checked="true"]',
  )
}

export async function getSettingModalSelectedPrivacyLevel(modalLabel: string) {
  const selectedElement = await getSettingModalSelectedOption(modalLabel)

  let privacyLevel: PrivacySetting
  if (selectedElement.role === 'radio' || selectedElement.role === 'none') {
    privacyLevel =
      selectAudienceUtils.getPrivacySettingFromOption(selectedElement)
  } else {
    privacyLevel =
      selectAudienceUtils.getPrivacySettingFromSurroundingText(selectedElement)
  }

  return privacyLevel
}

export async function closeSettingModal(
  modalLabel: string,
  actionName: string,
  options: {
    timeoutMs?: number
    removeModalManually: boolean
  } = {
    removeModalManually: false,
  },
) {
  let timeoutMs = options.timeoutMs
  if (timeoutMs === null || timeoutMs === undefined) {
    timeoutMs = 1000
  }

  const dialog = await getElement<HTMLElement>(
    document,
    `//*[@role="dialog" and descendant::*[contains(text(),"${modalLabel}")]]`,
  )

  let button
  switch (actionName) {
    case 'CANCEL':
      button = await getElement<HTMLElement>(
        dialog,
        `descendant::*[(${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'cancel',
        )} or ${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'close',
        )}) and @role="button"]`,
      )
      break
    case 'DONE':
      button = await getElement<HTMLElement>(
        dialog,
        `descendant::*[(${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'done',
        )} or ${getCaseInsensitiveXPathTextSelector(
          'aria-label',
          'save',
        )}) and @role="button"]`,
      )
      break
    default:
      throw new Error(
        `getActionButton: Only valid values for actionName {CANCEL, DONE} (received ${actionName})`,
      )
  }

  const dialogHasClosed = promises.getDomPromise(
    document,
    (node) => evaluateXpath(node, '//*[@role = "dialog"]').length === 0,
  )

  button.click()

  if (options.removeModalManually) {
    promises.wait(Math.min(timeoutMs / 2, 100)).then(() => {
      const modal = evaluateXpath<HTMLElement>(
        document,
        '//*[@role = "dialog"]',
      )[0]
      if (modal) {
        modal.remove()
      }
    })
  }

  await Promise.race([
    dialogHasClosed,
    promises.throwIfTimeout(
      `Modal did not close (waited ${timeoutMs})`,
      timeoutMs,
    ),
  ])
}

export async function setSettingModalPrivacyLevel(
  modalLabel: string,
  isDry: boolean,
  targetValue: PrivacySetting,
) {
  const dialog = await getElement<HTMLElement>(
    document,
    `//*[@role="dialog" and descendant::*[contains(text(),"${modalLabel}")]]`,
  )
  const curValue = await getSettingModalSelectedPrivacyLevel(modalLabel)

  if (
    !curValue ||
    (curValue.name === targetValue.name && curValue.level === targetValue.level)
  ) {
    return closeSettingModal(modalLabel, 'CANCEL')
  }

  if (!isPrivacySettingUndoable(targetValue)) {
    return closeSettingModal(modalLabel, 'CANCEL')
  }

  await clickRowMatchingPrivacySettingInDialog(dialog, targetValue)
  return closeSettingModal(modalLabel, isDry ? 'CANCEL' : 'DONE')
}
